import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, TouchableOpacity,
  FlatList,
  ScrollView,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSelector } from 'react-redux';
import Style from '../../constants/Style';
import CloseModalIcon from '../CloseModalIcon';
import Colors from '../../constants/Colors';
import FlagIcon from '../FlagIcon';
import isMobile from '../IsMobile';
import getPriorityColor from '../PriorityColorScheme';
import { occupancyColor } from './AreaView';
import TemplateIcon30 from '../30pxIcons/TemplateIcon30';
import IEQIcon from '../24pxIcons/IEQIcon';
import DoorIcon from '../DoorIcon';
import PersonIcon from '../PersonIcon';
import MeterIcon from '../12pxIcons/MeterIcon';

interface MapLegendProps {
  isVisible: boolean,
  hide: () => void,
  slaNames: string[],
}

const MapLegend = ({
  isVisible, hide, slaNames,
}: MapLegendProps) => {
  const [visibilityToggleEnabled, setVisibilityToggleEnabled] = useState<boolean>(true);

  const happyEnabled = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);

  useEffect(() => {
    if (isVisible) {
      setVisibilityToggleEnabled(false);
      const timeout = setTimeout(() => {
        setVisibilityToggleEnabled(true);
        clearTimeout(timeout);
      }, 500);
    }
  }, [isVisible]);

  const closeModal = () => (visibilityToggleEnabled ? hide() : null);

  type DataItem = {
    title: string, value: any | null,
    secondaryColor?: string | null, border?: string | null
  }

  const occupancyData: DataItem[] = [
    { title: 'Green - no one there (0%)', value: 0 },
    { title: 'Blue - someone there (1 - 50%)', value: 50 },
    { title: 'Yellow - occupied (51 - 70%)', value: 70 },
    { title: 'Red - a lot of people there (>70%)', value: 71 },
    { title: 'Gray - not applicable (N/A)', value: null },
    { title: '', value: null },
  ];

  const ieqData: DataItem[] = [
    { title: 'Good', value: Colors.light.hereworksGreen, secondaryColor: '#fff' },
    { title: 'Normal', value: Colors.light.hereworksBlue500, secondaryColor: '#fff' },
    { title: 'Moderate', value: Colors.light.hereworksYellow500, secondaryColor: '#fff' },
    { title: 'Bad', value: Colors.light.hereworksRed500, secondaryColor: '#fff' },
    {
      title: 'Offline',
      value: Colors.light.hereworksBlack100,
      secondaryColor: Colors.light.hereworksBlack300,
      border: Colors.light.hereworksBlack300,
    },
    { title: '', value: '#fff' },
  ];

  const pcsData: DataItem[] = [
    { title: 'No one there (0%)', value: Colors.light.hereworksGreen, secondaryColor: '#fff' },
    { title: 'Someone there (1 - 50%)', value: Colors.light.hereworksBlue500, secondaryColor: '#fff' },
    { title: 'A lot of people there (>70%)', value: Colors.light.hereworksYellow500, secondaryColor: '#fff' },
    {
      title: 'Offline', value: Colors.light.hereworksBlack100, secondaryColor: Colors.light.hereworksBlack300, border: Colors.light.hereworksBlack300,
    },
    { title: '', value: '#fff' },
    { title: '', value: '#fff' },
  ];

  const entData: DataItem[] = [
    { title: 'Online', value: Colors.light.hereworksGreen, secondaryColor: '#fff' },
    {
      title: 'Offline', value: Colors.light.hereworksBlack100, secondaryColor: Colors.light.hereworksBlack300, border: Colors.light.hereworksBlack300,
    },
    { title: '', value: '#fff', secondaryColor: '#fff' },
  ];

  return <Modal
    style={Style.modal}
    isVisible={isVisible}
    onBackButtonPress={closeModal}
    onBackdropPress={closeModal}
    onSwipeComplete={closeModal}
  >
    <View
      style={[Style.modalContent, styles.container]}
      testID='map-legend-modal-description'
    >
      <TouchableOpacity
        style={Style.closeButton}
        onPress={hide}
      >
        <CloseModalIcon />
      </TouchableOpacity>
      <Text style={styles.title}>{'Legend'}</Text>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} style={{ flex: 1 }}>
        <View style={{ flexDirection: isMobile ? 'column' : 'row', flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
          {(!isMobile && happyEnabled) ? <View style={styles.block}>
            <FlatList
              style={{ alignSelf: 'flex-start' }}
              ListHeaderComponent={<Text style={styles.sectionTitle}>{'Occupancy'}</Text>}
              data={occupancyData}
              renderItem={({ item }: { item: DataItem }) => <View style={styles.occupancyItem}>
                {item.title ? <>
                  <View style={[styles.square, { backgroundColor: occupancyColor(item.value) }]} />
                  <View style={[
                    styles.square, styles.squareSecondary,
                    { backgroundColor: occupancyColor(item.value, true) },
                  ]} />
                  <Text style={styles.text}>{item.title}</Text></> : null}
              </View>}
              testID='legend-occupancy'
            />
          </ View> : null}
          <View style={styles.block}>
            <View
              style={styles.ticketSection}
              testID='legend-priority'
            >
              <Text style={styles.sectionTitle}>{'Priority'}</Text>
              {[...Array(5).keys()].map((index: number) => <View
                key={`map_legend_item_${index}`}
                style={styles.priorityItem}
              >
                <View style={[styles.dot, { backgroundColor: getPriorityColor(index) }]} />
                <Text style={styles.text}>{`P${index + 1} - ${slaNames[index]}`}</Text>
              </View>)}
            </View>
          </View>
          <View style={styles.block}>
            <View
              style={styles.ticketSection}
              testID='legend-ticket'
            >
              <Text style={styles.sectionTitle}>{'Ticket'}</Text>
              <View style={styles.ticketItem}>
                <View style={styles.flagIcon}>
                  <FlagIcon color='#FFF' />
                </View>
                <Text style={styles.text}>{'Single ticket'}</Text>
              </View>
              <View style={styles.ticketItem}>
                <View style={styles.flagIcon}>
                  <FlagIcon color='#FFF' />
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{'3'}</Text>
                  </View>
                </View>
                <Text style={styles.text}>{'Multiple tickets'}</Text>
              </View>
            </View>
          </View>
          <View
            style={styles.block}
            testID='legend-assets'
          >
            <Text style={styles.sectionTitle}>{'Assets'}</Text>
            <View style={styles.ticketItem}>
              <View style={styles.flagIcon}>
                <TemplateIcon30 />
              </View>
              <Text style={styles.text}>{'Asset'}</Text>
            </View>
          </View>
        </View>
        {happyEnabled ? <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          <View
            style={styles.block}
            testID='legend-sensors-pcs'
          >
            <Text style={styles.sectionTitle}>{'Sensors'}</Text>
            <Text style={styles.subTitle}>{'People counting sensor (PCS)'}</Text>
            <FlatList
              style={{ alignSelf: 'flex-start' }}
              data={pcsData}
              renderItem={({ item }: { item: DataItem }) => {
                if (!item.title) return null;
                return (<View style={styles.occupancyItem}>
                  {item.title ? <View style={[{
                    backgroundColor: item.value,
                    borderColor: item.border ?? item.value,
                  }, styles.sensor]}>
                    <PersonIcon color={item.secondaryColor} fill={item.secondaryColor} />
                  </View> : null}
                  <Text style={styles.text}>{item.title}</Text>
                </View>);
              }}
            />
          </View>
          <View
            style={styles.block}
            testID='legend-sensors-mts'
          >
            {isMobile ? null : <Text style={styles.sectionTitle}>{' '}</Text>}
            <Text style={styles.subTitle}>{'IEQ Multisensor (MTS)'}</Text>
            <FlatList
              style={{ alignSelf: 'flex-start' }}
              data={ieqData}
              renderItem={({ item }: { item: DataItem }) => {
                if (isMobile && !item.title) return null;
                return (<View style={styles.occupancyItem}>
                  {item.title ? <View style={[{
                    backgroundColor: item.value,
                    borderColor: item.border ?? item.value,
                  }, styles.sensor]}>
                    <IEQIcon fill={item.secondaryColor} />
                  </View> : null}
                  <Text style={styles.text}>{item.title}</Text>
                </View>);
              }}
            />
          </View>
          <View
            style={styles.block}
            testID='legend-sensors-ent'
          >
            {isMobile ? null : <Text style={styles.sectionTitle}>{' '}</Text>}
            <Text style={styles.subTitle}>{'Entry (ENT)'}</Text>
            <FlatList
              data={entData}
              renderItem={({ item }: { item: DataItem }) => <View style={styles.occupancyItem}>
                {item.title ? <View style={[{
                  backgroundColor: item.value,
                  borderColor: item.border ?? item.value,
                }, styles.sensor]}>
                  <DoorIcon color={item.secondaryColor} fill={item.secondaryColor} />
                </View> : null}
                <Text style={styles.text}>{item.title}</Text>
              </View>}
            />
          </View>
          <View
            style={styles.block}
            testID='legend-sensors-pir'
          >
            {isMobile ? null : <Text style={styles.sectionTitle}>{' '}</Text>}
            <Text style={styles.subTitle}>{'PIR sensor'}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginBottom: 16 }}>
              <View style={[styles.pir, { borderColor: Colors.light.hereworksGreen500 }]}>
                <View style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: Colors.light.hereworksGreen500,
                }} />
              </View>
              <Text style={styles.text}>{'Clear'}</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginBottom: 16 }}>
              <View style={[styles.pir, { borderColor: Colors.light.hereworksBlack400 }]}>
                <View style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: Colors.light.hereworksBlack400,
                }} />
              </View>
              <Text style={styles.text}>{'No activity'}</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginBottom: 16 }}>
              <View style={[styles.pir, { borderColor: Colors.light.hereworksRed500 }]}>
                <View style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: Colors.light.hereworksRed500,
                }} />
              </View>
              <Text style={styles.text}>{'Motion detected in the last 20 minutes'}</Text>
            </View>
          </View>
          <View
            style={styles.block}
            testID='legend-sensors-meters'
          >
            {isMobile ? null : <Text style={styles.sectionTitle}>{' '}</Text>}
            <Text style={styles.subTitle}>{'Meters'}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
              <View style={[styles.meter, { backgroundColor: Colors.light.hereworksGreen500 }]}>
                <MeterIcon fill={'#fff'} />
              </View>
              <Text style={styles.text}>{'Online'}</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
              <View style={[styles.meter, { backgroundColor: Colors.light.hereworksBlack100 }]}>
                <MeterIcon fill={Colors.light.hereworksBlack300} />
              </View>
              <Text style={styles.text}>{'Offline'}</Text>
            </View>
          </View>
        </View> : null}
      </ScrollView>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  title: {
    fontSize: 28,
    lineHeight: 42,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    textAlign: 'center',
    alignSelf: 'center',
    marginBottom: 40,
  },
  container: {
    paddingHorizontal: isMobile ? 20 : 0,
    paddingBottom: isMobile ? 12 : 28,
    paddingTop: isMobile ? 20 : 60,
    minWidth: isMobile ? '90%' : 360,
    margin: isMobile ? 10 : 100,
    marginTop: isMobile ? 50 : 100,
    flex: 1,
    flexWrap: 'wrap',
  },
  content: {
    flexDirection: 'row',
  },
  ticketItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  priorityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  ticketSection: {
    flex: isMobile ? 1 : undefined,
  },
  sectionTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginBottom: 16,
  },
  subTitle: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 8,
  },
  dot: {
    width: 16,
    aspectRatio: 1,
    borderRadius: 8,
    marginEnd: 8,
  },
  text: {
    fontSize: 14,
    lineHeight: 16.41,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  flagIcon: {
    marginEnd: isMobile ? 8 : 16,
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderRadius: 24,
    width: 48,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    shadowOpacity: 0.4,
    shadowRadius: 5,
    shadowColor: 'black',
    elevation: 5,
    borderWidth: 2,
    borderColor: '#fff',
  },
  badge: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksRed500,
    top: -4,
    start: -4,
  },
  badgeText: {
    fontSize: 14,
    lineHeight: 20,
    color: '#FFF',
    fontFamily: 'Roboto_700Bold',
  },
  square: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksPrimaryGrey,
    marginEnd: 4,
  },
  squareSecondary: {
    marginEnd: 8,
  },
  occupancyItem: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  sensor: {
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
    width: 24,
    height: 24,
    borderRadius: 4,
    shadowOpacity: 0.4,
    shadowRadius: 5,
    shadowColor: 'black',
    elevation: 5,
    margin: 4,
    borderWidth: 1,
  },
  pir: {
    marginRight: 8,
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    padding: 6,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  block: { paddingHorizontal: 40 },
  meter: {
    width: 24,
    height: 24,
    borderRadius: 4,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default MapLegend;
